import { useHistory } from 'react-router-dom';

import {
  EPackages,
  getAddOns,
  getFeatureMap,
  getServices,
  type IAdsFeatures,
  type IMonetizationFeatures,
  type TFlags,
} from '@feathr/blackbox';
import { useLocalUrl } from '@feathr/extender/state';
import { toBoolean } from '@feathr/extender/utils';
import { useAccount, useFlags, useUser } from '@feathr/report_components';

import { createCampaignRecord } from './useLicense.campaigns';
import { applyExceptions } from './useLicense.exceptions';
import type { TLicenseFeatures } from './useLicense.types';
import { assertActivePackage } from './useLicense.utils';

type TLicenseQuery = keyof TLicenseFeatures | Array<keyof TLicenseFeatures>;

export default function useLicense(): TLicenseFeatures;
export default function useLicense<K extends keyof TLicenseFeatures>(query: K): TLicenseFeatures[K];
export default function useLicense<K extends keyof TLicenseFeatures>(
  query: K[],
): Pick<TLicenseFeatures, K>;
export default function useLicense(
  query?: TLicenseQuery,
):
  | TLicenseFeatures
  | TLicenseFeatures[keyof TLicenseFeatures]
  | Pick<TLicenseFeatures, keyof TLicenseFeatures> {
  const history = useHistory();
  const localUrl = useLocalUrl();

  const account = useAccount();
  const { isInternal } = useUser();
  const { overridePackageImplementationsAccess, showDripCampaigns, showProResources } = useFlags();

  // Does not throw error if the user is internal.
  assertActivePackage(account.activePackage, isInternal, history, localUrl);

  const {
    ads,
    email,
    conversations,
    other,
    data,
    creatives,
    invites,
    monetization,
    trainingAndSupport,
  } = getFeatureMap(
    // If account package is invalid and user is internal, use the internal package.
    !account.hasValidLicensePackageName && isInternal
      ? EPackages.Internal
      : account.activePackage.name,
  );
  const services = getServices(account.activeServices.map((s) => s.item_id));
  const addOns = getAddOns(account.activeAddOns.map((a) => a.item_id));

  const campaigns = createCampaignRecord(ads, email, conversations, other, invites, monetization);

  const hasExpansionCampaigns = Object.values(ads).every((adsFeature: IAdsFeatures) =>
    toBoolean(adsFeature),
  );

  const hasMonetization = Object.values(monetization).every(
    (monetizationFeature: IMonetizationFeatures) => toBoolean(monetizationFeature),
  );

  const exceptionFlags = [
    overridePackageImplementationsAccess ? 'overridePackageImplementationsAccess' : null,
    // temporary override until refactor of useCampaignPermissions
    showDripCampaigns ? 'showDripCampaigns' : null,
    showProResources ? 'showProResources' : null,
  ].filter(Boolean) as TFlags[];

  const license = applyExceptions({
    license: {
      ...ads,
      ...email,
      ...conversations,
      ...other,
      ...data,
      ...creatives,
      ...invites,
      ...monetization,
      ...trainingAndSupport,
      ...campaigns,
      ...services,
      ...addOns,
      hasApiKeys: data.zapier > 0,
      hasConflictingIntegrations: account.hasConflictingIntegrations,
      hasExpansionCampaigns,
      hasMonetization,
      hasDefaultFlightPaths: !account.isPlatformLite,
    },
    activeServices: account.activeServices,
    flags: exceptionFlags,
  });

  if (!query) {
    return license;
  }

  if (Array.isArray(query)) {
    return Object.fromEntries(query.map((key) => [key, license[key]])) as Pick<
      TLicenseFeatures,
      keyof TLicenseFeatures
    >;
  }

  return license[query];
}
